import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cx } from '../../../internal/cx.js';
import { VisuallyHidden } from '../../layout/visually-hidden/index.js';
/** Number of minor-unit digits for an ISO 4217 currency (USD 2, JPY 0, KWD 3). */
export function currencyDigits(currency) {
    return (new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2);
}
/** Converts a minor-unit amount to an exact decimal string without floating-point loss. */
function minorToDecimal(amount, digits) {
    let raw;
    if (typeof amount === 'bigint')
        raw = amount.toString();
    else if (typeof amount === 'number')
        raw = Math.round(amount).toString();
    else
        raw = amount.trim().replace(/\.0*$/, '');
    if (!/^-?\d+$/.test(raw))
        throw new RangeError(`Money: minor units must be an integer, got ${raw}`);
    if (digits === 0)
        return raw;
    const negative = raw.startsWith('-');
    const digitsOnly = (negative ? raw.slice(1) : raw).padStart(digits + 1, '0');
    const whole = digitsOnly.slice(0, -digits);
    const fraction = digitsOnly.slice(-digits);
    return `${negative ? '-' : ''}${whole}.${fraction}`;
}
/** The value `Intl.NumberFormat` receives: exact decimal strings are passed through. */
function toFormattable(amount, currency, minorUnits) {
    if (minorUnits)
        return minorToDecimal(amount, currencyDigits(currency));
    return typeof amount === 'string' ? amount.trim() : amount;
}
function isNegative(value) {
    return typeof value === 'string' ? value.trim().startsWith('-') : value < 0;
}
/** Formats an amount of one currency with `Intl.NumberFormat`. */
export function formatMoney(amount, currency, options = {}) {
    const { locale, minorUnits = false, notation = 'standard', ...intl } = options;
    const value = toFormattable(amount, currency, minorUnits);
    const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        notation,
        ...intl,
    });
    // ES2023 Intl formats decimal strings exactly; the ES2022 lib types do not know that yet.
    return formatter.format(value);
}
/**
 * A formatted amount in tabular figures. With `notation="compact"` the short
 * form is shown and the full amount is given to assistive technology and as
 * a hover title.
 */
export const Money = forwardRef(function Money({ amount, currency, locale, minorUnits, notation = 'standard', signDisplay, currencyDisplay, currencySign, minimumFractionDigits, maximumFractionDigits, toneNegative = false, emptyLabel, className, title, ...rest }, ref) {
    if (amount === null || amount === undefined) {
        return (_jsx("span", { ref: ref, ...rest, className: cx('mn-money', className), "data-empty": "", children: emptyLabel ?? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, children: "\u2014" }), _jsx(VisuallyHidden, { children: "No amount" })] })) }));
    }
    const options = {
        ...(locale !== undefined && { locale }),
        ...(minorUnits !== undefined && { minorUnits }),
        ...(signDisplay !== undefined && { signDisplay }),
        ...(currencyDisplay !== undefined && { currencyDisplay }),
        ...(currencySign !== undefined && { currencySign }),
        ...(minimumFractionDigits !== undefined && { minimumFractionDigits }),
        ...(maximumFractionDigits !== undefined && { maximumFractionDigits }),
    };
    const shown = formatMoney(amount, currency, { ...options, notation });
    const compact = notation === 'compact';
    const full = compact ? formatMoney(amount, currency, options) : shown;
    const negative = isNegative(amount);
    return (_jsx("span", { ref: ref, ...rest, className: cx('mn-money', className), title: title ?? (compact && full !== shown ? full : undefined), "data-currency": currency, "data-notation": notation, "data-negative": negative || undefined, "data-tone": toneNegative && negative ? 'danger' : undefined, children: compact && full !== shown ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": true, children: shown }), _jsx(VisuallyHidden, { children: full })] })) : (shown) }));
});
//# sourceMappingURL=money.js.map